import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { describeError, type FriendlyError } from '@london-life/errors';
import { Button } from '@london-life/ui/components/button';
import { Checkbox } from '@london-life/ui/components/checkbox';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { api } from '../lib/api';
import { CodeForm } from './code-form';
import { RecoveryCodes } from './recovery-codes';

// first sign-in after two-step login became required: scan, confirm a code, save the recovery codes
export function TwoStepSetup({ onDone, onSignOut }: { onDone: () => void; onSignOut: () => void }) {
  const [setup, setSetup] = useState<{ secret: string; qr: string } | null>(null);
  const [failure, setFailure] = useState<FriendlyError | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);
  const [saved, setSaved] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let alive = true;
    api.admin.twoStep
      .setup()
      .then(async (reply) => {
        const qr = await QRCode.toDataURL(reply.otpauthUri, { margin: 1, width: 220 });
        if (alive) setSetup({ secret: reply.secret, qr });
      })
      .catch((error: unknown) => alive && setFailure(describeError(error)));
    return () => {
      alive = false;
    };
  }, [attempt]);

  if (codes)
    return (
      <div className="two-step-card">
        <h1>Save your recovery codes</h1>
        <p className="admin-muted">
          If you lose your phone, each of these codes lets you sign in once. This is the only time they are shown.
        </p>
        <RecoveryCodes codes={codes} />
        <label className="admin-checklist-item">
          <Checkbox checked={saved} onCheckedChange={(state) => setSaved(state === true)} />
          <span>I have saved these codes somewhere safe</span>
        </label>
        <Button disabled={!saved} onClick={onDone}>
          Continue to the dashboard
        </Button>
      </div>
    );
  return (
    <div className="two-step-card">
      <h1>Set up two-step login</h1>
      <p className="admin-muted">
        Every admin needs a code from an authenticator app as well as their password. Use Google Authenticator,
        Microsoft Authenticator, Authy or 1Password.
      </p>
      {failure && <ErrorNotice error={failure} onRetry={() => (setFailure(null), setAttempt((count) => count + 1))} />}
      {setup && (
        <>
          <ol className="two-step-steps">
            <li>Open your authenticator app and add a new account.</li>
            <li>Scan this QR code, or type the key below.</li>
          </ol>
          <img
            className="two-step-qr"
            src={setup.qr}
            alt="QR code for your authenticator app"
            width={220}
            height={220}
          />
          <p className="two-step-secret">
            Key: <code>{setup.secret.replace(/(.{4})/g, '$1 ').trim()}</code>
          </p>
          <p className="admin-muted">3. Enter the 6-digit code the app shows.</p>
          <CodeForm
            submitLabel="Turn on two-step login"
            allowRecovery={false}
            onSubmit={async (body) => setCodes((await api.admin.twoStep.confirm(body)).recoveryCodes)}
          />
        </>
      )}
      <button type="button" className="two-step-switch" onClick={onSignOut}>
        Sign out
      </button>
    </div>
  );
}
